//src/admin/pages/AdminMakeSale.js
import React, { useEffect, useState } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
  getAdminProducts,
  getAdminCategoriesWithProducts,
  makeProductsOnSale,
} from "../../services/adminProducts.api";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheckCircle, FaTimes } from "react-icons/fa";

export default function AdminMakeSale() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [salePercent, setSalePercent] = useState(10);
  const [selected, setSelected] = useState([]);

  const [toast, setToast] = useState(null); // { type: "success"|"error", text: string }

  useEffect(() => {
    getAdminProducts().then((data) => {
      setProducts(data.filter((p) => !p.on_sale));
    });

    getAdminCategoriesWithProducts().then(setCategories);
  }, []);

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();

    const matchSearch =
      (p.name_en || "").toLowerCase().includes(q) ||
      (p.public_id || "").toLowerCase().includes(q);

    const productCategories = Array.isArray(p.categories)
  ? p.categories
  : typeof p.categories === "string"
  ? p.categories.split(",")
  : [];

const matchCategory = !category || productCategories.includes(category);


    return matchSearch && matchCategory;
  });

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applySale = async () => {
    if (!salePercent || selected.length === 0) return;

    try {
      await makeProductsOnSale(selected, salePercent);

      setToast({
        type: "success",
        text: "Sale applied successfully ✅",
      });

      // remove updated products from the list
      setProducts((prev) => prev.filter((p) => !selected.includes(p.id)));
      setSelected([]);

      // auto-hide toast
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast({
        type: "error",
        text: "Failed to apply sale. Please try again.",
      });
      setTimeout(() => setToast(null), 3000);
    }
  };

const allFilteredIds = filtered.map((p) => p.id);

const isAllSelected =
  allFilteredIds.length > 0 &&
  allFilteredIds.every((id) => selected.includes(id));

const toggleSelectAll = () => {
  if (isAllSelected) {
    // unselect only filtered ones
    setSelected((prev) =>
      prev.filter((id) => !allFilteredIds.includes(id))
    );
  } else {
    // add missing filtered ones
    setSelected((prev) => [
      ...prev,
      ...allFilteredIds.filter((id) => !prev.includes(id)),
    ]);
  }
};


  return (
    <div className="flex bg-gray-100 min-h-screen">
      <AdminSidebar />
      <div className="flex-1 lg:ml-64">
        <AdminHeader />

        <main className="p-6 space-y-4">
          {/* Top row: Back button (NO page title) */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/admin/products/on-sale")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border shadow-sm hover:bg-gray-50"
            >
              <FaArrowLeft />
              Back
            </button>

            <div className="text-sm text-gray-500">
  Total:{" "}
  <span className="font-semibold text-gray-700">
    {products.length}
  </span>{" "}
  · Showing:{" "}
  <span className="font-semibold text-gray-700">
    {filtered.length}
  </span>
</div>

          </div>

          {/* Toast */}
          {toast && (
            <div className="fixed top-5 right-5 z-50">
              <div
                className={`flex items-start gap-3 rounded-xl shadow-lg px-4 py-3 bg-white border ${
                  toast.type === "success"
                    ? "border-green-200"
                    : "border-red-200"
                }`}
              >
                <div
                  className={`mt-0.5 ${
                    toast.type === "success" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {toast.type === "success" ? <FaCheckCircle /> : <FaTimes />}
                </div>

                <div className="text-sm text-gray-700 font-medium">
                  {toast.text}
                </div>

                <button
                  className="ml-2 text-gray-400 hover:text-gray-600"
                  onClick={() => setToast(null)}
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              className="p-3 border rounded-xl"
              placeholder="Search by name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="p-3 border rounded-xl"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug}>
                  {c.name_en}
                </option>
              ))}
            </select>

            <input
              type="number"
              min="1"
              max="90"
              className="p-3 border rounded-xl"
              value={salePercent}
              onChange={(e) => setSalePercent(e.target.value)}
              placeholder="Sale %"
            />

            <button
              onClick={applySale}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold py-3"
            >
              Make it on Sale
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 text-center">
  <input
    type="checkbox"
    className="w-4 h-4 accent-red-600"
    checked={isAllSelected}
    onChange={toggleSelectAll}
    title={isAllSelected ? "Unselect all" : "Select all"}
  />
</th>

                  <th className="p-3">Name</th>
                  <th className="p-3">Public ID</th>
                  <th className="p-3">Price</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-red-600"
                        checked={selected.includes(p.id)}
                        onChange={() => toggle(p.id)}
                      />
                    </td>
                    <td className="p-3 font-medium">{p.name_en}</td>
                    <td className="p-3 text-gray-500">{p.public_id}</td>
                    <td className="p-3">${Number(p.base_price).toFixed(2)}</td>
                  </tr>
                ))}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="4" className="p-6 text-center text-gray-500">
                      No products found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
}

